import * as React from 'react';
import { Flex, Radio, RadioGroup, RadioProps } from '@chakra-ui/react';

import { UndoEdit } from '../../iconButtons/UndoEdit';
import { IQuestion } from '../../../types';
import { withConfirm } from '../withConfirm';
import { CustomFormControl, DirectionProp } from '../CustomFormControl';

export interface IProps extends DirectionProp {
  questions: ReadonlyArray<IQuestion>;
  id?: string;
  name?: string;
  isInline?: boolean;
  maxQuestions?: number;
  label?: string;
  subText?: string | React.ReactNode;
  errorText?: string;
  hideErrorText?: boolean;
  forwardedRef?: ((instance: HTMLInputElement | null) => void) | React.RefObject<HTMLInputElement> | null | undefined;
  inputHelperText?: string;
  onClick?: (question: IQuestion) => void;
  onBlur?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  canUndo?: boolean;
  onUndo?: () => void;
  size?: 'lg' | 'md' | 'sm';
  mb?: number | string;
  radioProps?: RadioProps;
  isDisabled?: boolean;
}

const RadioOptions: React.FC<IProps> = (props: IProps) => {
  const {
    questions,
    id,
    name,
    isInline,
    maxQuestions,
    label,
    subText,
    errorText,
    hideErrorText,
    forwardedRef,
    inputHelperText,
    onClick,
    canUndo = false,
    onUndo,
    size = 'lg',
    direction,
    radioProps,
    isDisabled = false,
  } = props;
  const dataId = id || 'question';

  return (
    <CustomFormControl
      id={dataId}
      direction={direction}
      size={size}
      label={label}
      labelProps={{ as: 'legend' }}
      controlProps={{
        isInvalid: !hideErrorText && !!errorText,
      }}
      subLabel={subText}
      error={errorText}
      forwardedRef={forwardedRef}
      helperText={inputHelperText}
    >
      <Flex gap={{ base: 1, lg: 2 }}>
        <RadioGroup
          name={name}
          variant="outline"
          size={size}
          display="flex"
          rowGap={2}
          columnGap={{ base: 2, lg: 4 }}
          flexDirection={isInline ? 'row' : 'column'}
          flexGrow="1"
          isDisabled={isDisabled}
        >
          {questions &&
            questions.map((question, index) => {
              const id = `question-${dataId}-${index + 1}`;

              if (maxQuestions && index + 1 > maxQuestions) {
                return null;
              }

              return (
                <Radio
                  key={id}
                  id={id}
                  name={`${name}_${question.char}`}
                  isChecked={question.selected}
                  onChange={() => onClick!(question)}
                  className={`question-${index + 1}`}
                  data-selector={`${dataId || 'question'}-button-${index + 1}`}
                  flexGrow={isInline ? '1' : 'initial'}
                  width={isInline ? '100%' : 'initial'}
                  {...radioProps}
                >
                  {question.labelHtml || question.label}
                </Radio>
              );
            })}
        </RadioGroup>

        {canUndo ? <UndoEdit id={dataId} onUndo={onUndo} /> : null}
      </Flex>
    </CustomFormControl>
  );
};

const RadioOptionsWithConfirm = withConfirm(RadioOptions);

export { RadioOptions, RadioOptionsWithConfirm };
